import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Check, Loader2, X } from "lucide-react";
import type { FeedTask } from "../contexts/FeedPostContext";
import { playChipFlight } from "../utils/dustTransfer";

export const TOAST_DISMISS_MS = 3000;
const ENTER_MS = 160;
const EXIT_MS = 140;

const LABELS: Record<FeedTask["kind"], Record<FeedTask["status"], string>> = {
  post: { working: "Posting", done: "Posted", failed: "Couldn't post" },
  remove: { working: "Removing", done: "Removed", failed: "Couldn't remove" },
};

function canAnimate(el: HTMLElement | null): el is HTMLElement {
  return !!el?.animate && !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function FeedPostToast({ task, onDismiss }: { task: FeedTask; onDismiss: (key: number) => void }) {
  const ref = useRef<HTMLButtonElement>(null);
  const leaving = useRef(false);
  const entered = useRef(false);
  const { key, kind, status, error, origin } = task;
  const working = status === "working";
  const failed = status === "failed";

  // The toast is laid out in its corner and flies in from where it was started.
  useLayoutEffect(() => {
    const el = ref.current;
    if (entered.current || !canAnimate(el)) return;
    entered.current = true;
    if (origin) {
      playChipFlight(origin, el);
      return;
    }
    el.animate(
      [{ transform: "translateY(-8px)", opacity: 0 }, { transform: "none", opacity: 1 }],
      { duration: ENTER_MS, easing: "ease-out" },
    );
    // Runs once: the toast only ever flies in when it first appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leave = useCallback(() => {
    if (leaving.current) return;
    leaving.current = true;
    const el = ref.current;
    if (!canAnimate(el)) { onDismiss(key); return; }
    el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: EXIT_MS, easing: "ease-in", fill: "forwards" });
    // A timer, because a background tab holds back an animation's finish event.
    window.setTimeout(() => onDismiss(key), EXIT_MS);
  }, [key, onDismiss]);

  useEffect(() => {
    if (status !== "done") return;
    const timer = window.setTimeout(leave, TOAST_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [status, leave]);

  return (
    <button
      ref={ref}
      type="button"
      disabled={working}
      onClick={leave}
      className={`flex min-h-[36px] max-w-full items-center gap-1.5 border border-line bg-raised px-3 py-1.5 text-left text-xs font-medium text-content shadow-lg hover:bg-hover active:bg-pressed focus:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:min-w-[160px] sm:gap-2 sm:rounded-lg sm:px-4 sm:py-3 sm:text-sm disabled:hover:bg-raised ${
        working ? "pointer-events-none" : "pointer-events-auto"
      } ${failed ? "rounded-lg" : "rounded-full"}`}
    >
      {working && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted sm:h-4 sm:w-4" aria-hidden="true" />}
      {status === "done" && <Check className="h-3.5 w-3.5 shrink-0 text-success sm:h-4 sm:w-4" aria-hidden="true" />}
      {failed && <X className="h-3.5 w-3.5 shrink-0 text-danger sm:h-4 sm:w-4" aria-hidden="true" />}
      <span className="min-w-0">
        <span className="block">{LABELS[kind][status]}</span>
        {error && <span className="block font-normal leading-4 text-muted">{error}</span>}
        {!working && <span className="sr-only"> Dismiss.</span>}
      </span>
    </button>
  );
}

/** Feed post progress in the top right corner. Only finished toasts take taps. */
export default function FeedPostToasts({ tasks, onDismiss }: { tasks: FeedTask[]; onDismiss: (key: number) => void }) {
  return (
    <div
      role="status"
      className="pointer-events-none fixed right-3 z-[110] flex max-w-[calc(100vw-1.5rem)] flex-col items-end gap-2 sm:right-4 sm:max-w-sm"
      style={{ top: "max(0.75rem, env(safe-area-inset-top, 0px))" }}
    >
      {tasks.map((task) => <FeedPostToast key={task.key} task={task} onDismiss={onDismiss} />)}
    </div>
  );
}
